import React, { useState, useEffect, useRef } from 'react';
import { trackSmsRequested, trackSmsVerified } from '../utils/metrika';
import { useLegalLinks } from '../legal';
import { HeroIcon } from './NavIcons';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Inline-форма SMS-авторизации для встраивания прямо в баннер чекаута/подписки.
 * Управляет ввод телефона → ввод кода → сохранение JWT → onSuccess(user).
 */
function SmsAuthInline({ onSuccess, onCancel }) {
  const legal = useLegalLinks();
  // '+7' зашит — работаем только с РФ. Пользователь вводит только 10 цифр после.
  const [phone, setPhone] = useState('7');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timerRef.current);
    }
  }, [resendTimer]);

  // Код из СМС подставляется сам (31.10): iPhone подсказывает его над клавиатурой (autoComplete
  // у поля), Chrome на Android читает строку «@домен #код» в конце СМС (WebOTP, текст —
  // backend web_auth.sms_send). Каждая новая СМС — новое ожидание, прежнее снимаем
  const [otpRound, setOtpRound] = useState(0);
  useEffect(() => {
    if (!codeSent || typeof window === 'undefined' || !('OTPCredential' in window)) return undefined;
    const ac = new AbortController();
    navigator.credentials.get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((otp) => { if (otp && otp.code) setCode(String(otp.code).replace(/\D/g, '').slice(0, 4)); })
      .catch(() => {});
    return () => ac.abort();
  }, [codeSent, otpRound]);

  // Авто-сабмит при вводе 4-й цифры. Ref отслеживает уже отправленный код,
  // чтобы не зацикливать запросы и не дублировать при повторе одного значения.
  const submittedCodeRef = useRef('');
  useEffect(() => {
    if (codeSent && code.length === 4 && !isLoading && submittedCodeRef.current !== code) {
      submittedCodeRef.current = code;
      handleVerifyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, codeSent, isLoading]);

  const formatPhone = (digits) => {
    if (!digits) return '';
    let result = '+7';
    if (digits.length > 1) result += ' (' + digits.substring(1, Math.min(4, digits.length));
    if (digits.length >= 4) result += ') ' + digits.substring(4, Math.min(7, digits.length));
    if (digits.length >= 7) result += '-' + digits.substring(7, Math.min(9, digits.length));
    if (digits.length >= 9) result += '-' + digits.substring(9, Math.min(11, digits.length));
    return result;
  };

  const handlePhoneChange = (value) => {
    let digits = value.replace(/\D/g, '');
    // Нормализация: 8XX → 7XX
    if (digits.startsWith('8')) digits = '7' + digits.substring(1);
    // '7' зашита — всегда сохраняем как префикс
    if (digits.length === 0 || !digits.startsWith('7')) {
      digits = '7' + digits;
    }
    // Лимит 11 цифр
    setPhone(digits.substring(0, 11));
  };

  // Backspace через разделители: если слева от курсора не цифра —
  // вручную удаляем предыдущую цифру, иначе маска сразу её вернёт.
  const handlePhoneKeyDown = (e) => {
    if (e.key !== 'Backspace') return;
    const el = e.target;
    const pos = el.selectionStart;
    if (pos !== el.selectionEnd || pos === 0) return;
    const val = el.value;
    if (/\d/.test(val[pos - 1])) return;
    e.preventDefault();
    let digitCount = 0;
    for (let i = 0; i < pos; i++) {
      if (/\d/.test(val[i])) digitCount++;
    }
    if (digitCount === 0) return;
    let d = phone.slice(0, digitCount - 1) + phone.slice(digitCount);
    if (d.length > 0 && !d.startsWith('7')) d = '7' + d;
    setPhone(d.substring(0, 11));
  };

  const handleSendCode = async () => {
    if (phone.length !== 11) {
      setError('Введите корректный номер телефона');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const formatted = '+' + phone;
      const res = await fetch(`${API_URL}/web-auth/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formatted }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Ошибка отправки кода');
      trackSmsRequested();
      setCodeSent(true);
      setOtpRound((n) => n + 1);
      setResendTimer(60);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 4) {
      setError('Введите 4-значный код');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const formatted = '+' + phone;
      const res = await fetch(`${API_URL}/web-auth/sms/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formatted, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Неверный код');
      trackSmsVerified();
      localStorage.setItem('stems_jwt', data.access_token);
      onSuccess(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '15px',
    fontFamily: 'inherit',
    fontWeight: 600,
    color: 'var(--text-color)',
    borderRadius: '8px',
    border: '1px solid #CFD8DC',
    background: '#fff',
    boxSizing: 'border-box',
  };

  const btnPrimary = {
    width: '100%',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    background: 'var(--primary-color)',
    color: 'var(--text-color)',
    border: 'none',
    borderRadius: '10px',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    opacity: isLoading ? 0.6 : 1,
    marginTop: '8px',
  };

  return (
    <div style={{ textAlign: 'left', position: 'relative' }}>
      {/* Ошибка с иконкой из общего набора. Блок авторизации ограничен 300px,
          поэтому размеры подобраны так, чтобы типовые сообщения («Неверный код.
          Осталось попыток: N») умещались в одну строку: некрупный
          нежирный текст, компактные отступы. */}
      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          width: 'fit-content', maxWidth: '100%', margin: '0 auto 12px',
          background: '#FFF5F5', color: '#C62828',
          border: '1px solid #F3D6D6',
          padding: '8px 12px', borderRadius: '10px',
          fontSize: '12px', fontWeight: 500, lineHeight: 1.3, textAlign: 'left',
        }}>
          <HeroIcon name="alert" size={14} color="currentColor" inline />
          <span>{error}</span>
        </div>
      )}

      {!codeSent ? (
        <div style={{ maxWidth: '260px', margin: '0 auto' }}>
          <label style={{ display: 'block', fontSize: '13px', color: '#555', marginBottom: '4px', textAlign: 'center' }}>Телефон</label>
          <div style={{ position: 'relative' }}>
            <input
              type="tel"
              value={formatPhone(phone)}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onKeyDown={handlePhoneKeyDown}
              placeholder="+7 (999) 888-22-22"
              maxLength={18}
              style={{ ...inputStyle, textAlign: 'center', paddingRight: '36px' }}
              autoFocus
            />
            {(phone.length > 1 || onCancel) && (
              <button
                type="button"
                onClick={() => {
                  if (phone.length > 1) {
                    setPhone('7');
                  } else if (onCancel) {
                    onCancel();
                  }
                }}
                aria-label={phone.length > 1 ? 'Очистить' : 'Закрыть'}
                style={{
                  position: 'absolute', top: '50%', right: '10px',
                  transform: 'translateY(-50%)',
                  width: '22px', height: '22px',
                  background: 'transparent', border: 'none',
                  color: '#999', fontSize: '20px', lineHeight: 1,
                  cursor: 'pointer', padding: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                ×
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleSendCode}
            disabled={isLoading || phone.length !== 11}
            style={{
              ...btnPrimary,
              opacity: (isLoading || phone.length !== 11) ? 0.5 : 1,
              cursor: (isLoading || phone.length !== 11) ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Отправка...' : 'Отправить код'}
          </button>
          <div style={{
            fontSize: '11px', color: '#888', lineHeight: 1.4,
            marginTop: '8px', textAlign: 'center',
          }}>
            Продолжая, вы даёте согласие на обработку персональных данных и принимаете{' '}
            <a
              href={legal.policyUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#666', textDecoration: 'underline' }}
            >
              политику конфиденциальности
            </a>
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: '260px', margin: '0 auto' }}>
          <div style={{ fontSize: '13px', color: '#555', marginBottom: '8px', textAlign: 'center' }}>
            Код отправлен на {formatPhone(phone)}
          </div>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            name="one-time-code"
            value={code}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 4);
              setCode(v);
              if (v.length < 4) submittedCodeRef.current = '';
              if (error) setError('');
            }}
            placeholder="X X X X"
            maxLength={4}
            /* Поле ровно под 4 цифры — во всю ширину оно смотрелось пустым */
            style={{
              ...inputStyle,
              width: '150px', margin: '0 auto', display: 'block',
              textAlign: 'center', fontSize: '20px',
              letterSpacing: '6px', textIndent: '6px',
            }}
            autoFocus
          />
          {isLoading && (
            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#888' }}>
              Проверка...
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            {resendTimer > 0 ? (
              <span style={{ fontSize: '12px', color: '#888' }}>
                Отправить повторно через {resendTimer} сек
              </span>
            ) : (
              <button
                type="button"
                onClick={() => { setCode(''); setError(''); handleSendCode(); }}
                style={{
                  background: 'none', border: 'none', color: '#888',
                  fontSize: '12px', cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                Отправить код повторно
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setCodeSent(false); setCode(''); setError(''); }}
            style={{
              width: '100%', padding: '8px', fontSize: '12px',
              background: 'none', border: 'none', color: '#888',
              cursor: 'pointer', textDecoration: 'underline', marginTop: '4px',
            }}
          >
            Изменить номер
          </button>
        </div>
      )}
    </div>
  );
}

export default SmsAuthInline;
